import { h } from 'preact';

/**
 * One or more lines of text rendered as an SVG that scales to fill its box.
 *
 * Unlike FitTextOneLine (which measures the live DOM inside a
 * requestAnimationFrame and bakes a px font-size into the markup) this component
 * is resolution independent: the text is placed in a viewBox that is exactly as
 * wide as the glyphs, so `width:100%; height:100%` + preserveAspectRatio makes
 * the browser scale it to whatever box it ends up in.
 *
 * That matters for label printing: the markup is serialized with outerHTML and
 * re-parsed inside the print iframe, where the real page is the physical label
 * (62x29mm, 60x86mm, ...). Nothing needs to know the label size up front, and
 * there is no measure-then-serialize race.
 *
 * Pass `text` for a single line, or `lines` (array) for a block that scales as
 * ONE unit — every line then shares the same font size, which is what an address
 * needs. Rendering the lines as separate FitTextSvg elements would instead size
 * each line to its own box, so a short "0483 Oslo" would dwarf the name above it.
 */

// Reference font size for the viewBox. Any value works — only the ratio between
// the measured text width and this height ends up in the viewBox.
const REF_FONT_SIZE = 100;
// Room for ascenders/descenders around the reference size.
const REF_LINE_HEIGHT = REF_FONT_SIZE * 1.28;
const REF_BASELINE = REF_FONT_SIZE;
const FONT_FAMILY = 'Arial, Helvetica, sans-serif';

const ALIGNMENT = {
    left: { anchor: 'start', x: 0, aspect: 'xMinYMid' },
    center: { anchor: 'middle', x: 0.5, aspect: 'xMidYMid' },
    right: { anchor: 'end', x: 1, aspect: 'xMaxYMid' },
};

/**
 * Width of `text` in units where the font-size is REF_FONT_SIZE.
 * Canvas measureText is synchronous and does not need layout, so this works
 * while the node is being rendered into a detached/offscreen container.
 */
function measureTextWidth(text, fontWeight) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        // No canvas (very old browser / jsdom): fall back to a rough average
        // glyph width so the label still prints something sensible.
        return text.length * REF_FONT_SIZE * 0.55;
    }
    ctx.font = `${fontWeight} ${REF_FONT_SIZE}px ${FONT_FAMILY}`;
    return ctx.measureText(text).width || text.length * REF_FONT_SIZE * 0.55;
}

const FitTextSvg = ({ text, lines, align = 'left', fontWeight = 'normal', className = '', style = '' }) => {
    const values = (Array.isArray(lines) ? lines : [text])
        .filter(line => (line || line === 0) && `${line}`.trim())
        .map(line => `${line}`);
    if (!values.length) return null;

    const { anchor, x, aspect } = ALIGNMENT[align] || ALIGNMENT.left;
    // The widest line defines the viewBox, so every line scales by the same
    // factor and they stay left/right aligned with each other.
    const width = Math.max(1, ...values.map(line => measureTextWidth(line, fontWeight)));
    const height = values.length * REF_LINE_HEIGHT;

    return (
        <svg
            class={className}
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio={`${aspect} meet`}
            style={`display:block; width:100%; height:100%; ${style}`}
        >
            {values.map((line, index) => (
                <text
                    key={line + index}
                    x={width * x}
                    y={(index * REF_LINE_HEIGHT) + REF_BASELINE}
                    text-anchor={anchor}
                    font-family={FONT_FAMILY}
                    font-size={REF_FONT_SIZE}
                    font-weight={fontWeight}
                    fill='#000000'
                >{line}</text>
            ))}
        </svg>
    );
};

export default FitTextSvg;
